import { normalize, schema } from 'normalizr'
import createCachedSelector from 're-reselect'
import {
  IMap,
  IServiceLocationObject,
  IServiceObject,
  IServicePricingType,
  IServicePricingTypeObject,
  IServiceState,
  IStoreState
} from '../../../types/index'
import * as constants from '../../constants/index'
import { IServiceActions } from '../../actions/services'
import { IPaymentMethods } from 'src/types/payment-methods'

const initialServiceListState: IServiceState = {
  items: {},
  metadata: {
    fetching: false,
    fetchingError: '',
    processing: false,
    processingError: '',
    activeColumns: [
      constants.SERVICE_COLUMN.ORGANIZATION,
      constants.SERVICE_COLUMN.PROGRAM,
      constants.SERVICE_COLUMN.INTERNALNAME,
      constants.SERVICE_COLUMN.NAME,
      constants.SERVICE_COLUMN.ICON,
      constants.SERVICE_COLUMN.MEMBERSHIP_REQUIRED,
      constants.SERVICE_COLUMN.VIEW_OPTIONS,
      constants.SERVICE_COLUMN.TRIP_LIMIT
    ],
    edit: {},
    archive: {},
    restore: {}
  },
  servicePricingDiscount: {
    data: [],
    error: '',
    fetching: false
  },
  filters: {},
  pricingType: [] as IServicePricingType[],
  fares: [] as IServicePricingType[],
  locationList: [] as IServiceLocationObject[],
  paymentMethods: [] as IPaymentMethods[],
  serviceDesignatedZone: [] as any[]
}

export function service(state: IServiceState = initialServiceListState, action: IServiceActions): IServiceState {
  switch (action.type) {
    case constants.SERVICE_LIST_REQUEST:
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: true
        }
      })
    case constants.SERVICE_LIST_SUCCESS: {
      const items = action.payload || {}
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false,
          fetchingError: ''
        },
        items: { ...items }
      })
    }
    case constants.SERVICE_LIST_TOTAL_SUCCESS: {
      const items = action.payload || {}
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false,
          fetchingError: ''
        },
        serviceListTotal: action.payload
      })
    }

    case constants.SERVICE_LIST_FAIL: {
      const error = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false,
          fetchingError: error
        }
      })
    }
    case constants.SERVICE_ITEM_REQUEST: {
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: true
        }
      })
    }
    case constants.SERVICE_ITEM_SUCCESS: {
      const item = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false,
          fetchingError: ''
        },
        items: {
          ...state.items,
          ...item
        }
      })
    }
    case constants.SERVICE_ITEM_FAIL: {
      const error = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false,
          fetchingError: error
        }
      })
    }
    case constants.SERVICE_LIST_UPDATE_COLUMNS: {
      const columns = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          activeColumns: columns
        }
      })
    }
    case constants.SERVICE_CREATE_REQUEST: {
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: true
        }
      })
    }
    case constants.SERVICE_CREATE_SUCCESS: {
      const service = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: false,
          processingError: '',
          edit: {
            ...state.metadata.edit,
            serviceId: service.id
          }
        },
        items: {
          ...state.items,
          [service.id]: service
        }
      })
    }
    case constants.SERVICE_CREATE_FAIL: {
      const error = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: false,
          processingError: error
        }
      })
    }
    case constants.SERVICE_ARCHIVE_MODAL_SHOW: {
      const serviceId = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          archive: {
            serviceId
          }
        }
      })
    }
    case constants.SERVICE_ARCHIVE_MODAL_HIDE: {
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: false,
          processingError: '',
          archive: {
            serviceId: undefined
          }
        }
      })
    }
    case constants.SERVICE_ARCHIVE_REQUEST: {
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: true
        }
      })
    }
    case constants.SERVICE_ARCHIVE_SUCCESS: {
      if (!state.metadata.archive.serviceId) {
        return state
      }
      const service = state.items[state.metadata.archive.serviceId]

      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: false,
          processingError: '',
          archive: {
            serviceId: undefined
          }
        },
        items: {
          ...state.items,
          [service.id]: {
            ...state.items[service.id],
            archived: true
          }
        }
      })
    }
    case constants.SERVICE_ARCHIVE_FAIL: {
      const error = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: false,
          processingError: error
        }
      })
    }
    case constants.SERVICE_RESTORE_MODAL_SHOW: {
      const serviceId = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          restore: {
            serviceId
          }
        }
      })
    }
    case constants.SERVICE_RESTORE_MODAL_HIDE: {
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: false,
          processingError: '',
          restore: {
            serviceId: undefined
          }
        }
      })
    }
    case constants.SERVICE_RESTORE_REQUEST: {
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: true
        }
      })
    }
    case constants.SERVICE_RESTORE_SUCCESS: {
      if (!state.metadata.restore.serviceId) {
        return state
      }
      const service = state.items[state.metadata.restore.serviceId]

      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: false,
          processingError: '',
          restore: {
            serviceId: undefined
          }
        },
        items: {
          ...state.items,
          [service.id]: {
            ...state.items[service.id],
            archived: false
          }
        }
      })
    }
    case constants.SERVICE_RESTORE_FAIL: {
      const error = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: false,
          processingError: error
        }
      })
    }
    case constants.SERVICE_SEARCH_REQUEST: {
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: true
        }
      })
    }
    case constants.UPDATE_SERVICE_FILTERS: {
      return Object.assign({}, state, {
        filters: { ...state.filters, ...action.payload }
      })
    }
    case constants.SERVICE_SEARCH_SUCCESS: {
      const data = action.payload
      const service = new schema.Entity('services')
      const serviceSchema = [service]
      const normalizedData = normalize(data, serviceSchema)
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false,
          fetchingError: ''
        },
        items: normalizedData.entities.services ? normalizedData.entities.services : {}
      })
    }
    case constants.SERVICE_SEARCH_FAIL: {
      const error = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false,
          fetchingError: error
        }
      })
    }
    case constants.SERVICE_EDIT_CLEAR_STATE: {
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          edit: {
            ...state.metadata.edit,
            serviceId: undefined
          }
        }
      })
    }
    case constants.SERVICE_EDIT_SET_SERVICE_ID: {
      const serviceId = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          edit: {
            ...state.metadata.edit,
            serviceId
          }
        }
      })
    }
    case constants.SERVICE_SELECT_PROVIDER: {
      const { serviceId, providerId } = action.payload
      let providers = state.items[serviceId].providers
      providers = !providers ? [providerId] : [...providers, providerId]
      return Object.assign({}, state, {
        items: {
          ...state.items,
          [serviceId]: {
            ...state.items[serviceId],
            providers
          }
        }
      })
    }
    case constants.SERVICE_REMOVE_PROVIDER: {
      const { providerId, serviceId, productsIds } = action.payload
      const serviceProviders = state.items[serviceId].providers || []
      const serviceProducts = state.items[serviceId].providerProducts || []
      return Object.assign({}, state, {
        items: {
          ...state.items,
          [serviceId]: {
            ...state.items[serviceId],
            providers: serviceProviders.filter(p => p !== providerId),
            providerProducts: serviceProducts.filter(pp => !productsIds.includes(pp.id))
          }
        }
      })
    }
    case constants.SERVICE_SELECT_PRODUCT: {
      const { product, serviceId } = action.payload
      let products = state.items[serviceId].providerProducts
      products = products ? products : []
      products.push({ ...product })

      return Object.assign({}, state, {
        items: {
          ...state.items,
          [serviceId]: {
            ...state.items[serviceId],
            providerProducts: products
          }
        }
      })
    }
    case constants.SERVICE_REMOVE_PRODUCT: {
      const { productId, serviceId } = action.payload
      const items = { ...state.items }
      const service = { ...state.items[serviceId] }
      const products = service.providerProducts ? service.providerProducts.slice(0) : []
      service.providerProducts = products.filter(pp => pp.id !== productId)

      items[serviceId] = service

      return Object.assign({}, state, {
        items: { ...items }
      })
    }
    case constants.WORKING_HOURS_SET_ALL_DAYS_ACTIVE: {
      const { isAllDaysActive, id } = action.payload
      const items = { ...state.items }
      const service = { ...state.items[id] }
      service.isAllDaysActive = isAllDaysActive
      items[id] = service

      return Object.assign({}, state, {
        items: { ...items }
      })
    }
    //PricingType Reducers
    case constants.SERVICE_PRICING_TYPE_REQUEST_SUCCESS: {
      return Object.assign({}, state, {
        isFetching: false,
        pricingType: action.payload
      })
    }

    //PricingType Reducers Edit All
    case constants.SERVICE_PRICING_TYPE_EDIT_SUCCESS: {
      return Object.assign({}, state, {
        isFetching: false,
        fares: [...state.fares]
      })
    }

    case constants.SERVICE_LOCATION_LIST_SUCCESS: {
      return Object.assign({}, state, {
        isFetching: false,
        locationList: action.payload
      })
    }

    //DESIGNATED ZONE HOTSPOTS
    case constants.SERVICE_HOTSPOTS_LIST_SUCCESS: {
      return Object.assign({}, state, {
        isFetching: false,
        serviceDesignatedZone: action.payload
      })
    }

    case constants.SERVICE_HOTSPOTS_LIST_FAIL: {
      return Object.assign({}, state, {
        isFetching: false
      })
    }

    case constants.SERVICE_HOTSPOTS_UPDATE_SUCCESS_MESSAGE: {
      console.log('shit', action)
      return Object.assign({}, state, {
        isFetching: false,
        success: action.payload
      })
    }
    case constants.SERVICE_HOTSPOTS_UPDATE_FAIL: {
      return Object.assign({}, state, {
        isFetching: false,
        errors: 'Hotspots failed to update'
      })
    }

    case constants.SERVICE_DESIGNATED_HOTSPOT_DELETE_SUCCESS_MESSAGE: {
      return Object.assign({}, state, {
        isFetching: false,
        success: action.payload
      })
    }
    case constants.SERVICE_DESIGNATED_HOTSPOT_DELETE_FAIL: {
      return Object.assign({}, state, {
        isFetching: false,
        errors: action.payload
      })
    }

    case constants.SERVICE_LOCATION_LIST_REQUEST: {
      return Object.assign({}, state, {
        isFetching: true
      })
    }

    case constants.SERVICE_CREATE_LOCATION_SUCCESS: {
      const location = action.payload as IServiceLocationObject
      const items = Object.assign({}, state.locationList)
      items[location.id!] = Object.assign({}, items[location.id!], location)
      return Object.assign({}, state, { items })
    }

    case constants.SERVICE_DELETE_LOCATION_SUCCESS: {
      const { id } = action.payload
      delete state.locationList[id]
      return Object.assign({}, state, {
        lastUpdated: Date.now()
      })
    }

    //PricingType
    case constants.PAYMENT_METHODS_LIST_SUCCESS: {
      return Object.assign({}, state, {
        isFetching: false,
        paymentMethods: action.payload
      })
    }
    case constants.PAYMENT_METHODS_ADD_SUCCESS: {
      return Object.assign({}, state, {
        isFetching: false,
        paymentMethods: [...state.paymentMethods, action.payload]
      })
    }

    // SERVICE PRICING DISCOUNTS
    case constants.SERVICE_PRICING_DISCOUNT_REQUEST: {
      return Object.assign({}, state, {
        servicePricingDiscount: {
          fetching: true,
          data: [],
          error: ''
        }
      })
    }
    case constants.SERVICE_PRICING_DISCOUNT_SUCCESS: {
      return Object.assign({}, state, {
        servicePricingDiscount: {
          fetching: false,
          data: action.payload,
          error: ''
        }
      })
    }
    case constants.SERVICE_PRICING_DISCOUNT_FAIL: {
      return Object.assign({}, state, {
        servicePricingDiscount: {
          fetching: false,
          data: [],
          error: action.payload
        }
      })
    }
    default:
      return state
  }
}

const serviceItem = (state: IStoreState, serviceId: string) => state.service.items[serviceId]

export const getService = (state: IStoreState, serviceId: string) => state.service.items[serviceId]

export const getServiceFetching = (state: IStoreState): boolean => state.service.metadata.fetching

export const getServiceFetchingError = (state: IStoreState): string => state.service.metadata.fetchingError

export const getServiceProcessing = (state: IStoreState): boolean => state.service.metadata.processing

export const getServiceProcessingError = (state: IStoreState): string => state.service.metadata.processingError

export const getServiceListColumns = (state: IStoreState): string[] => state.service.metadata.activeColumns

export const getServiceArchiveServiceId = (state: IStoreState): string | undefined =>
  state.service.metadata.archive.serviceId

export const getServiceRestoreServiceId = (state: IStoreState): string | undefined =>
  state.service.metadata.restore.serviceId



// WEBPACK FOOTER //
// ./src/state/reducers/services/index.ts